import type { Climb } from '../../types/climb';

/** The easiest and hardest thing on one scale. */
export interface GradeRange {
  /** Which scale it is measured on — a crag grades routes and boulders apart. */
  type: 'Route' | 'Boulder';
  easiest: string;
  hardest: string;
}

/**
 * How hard a set of climbs runs, per scale.
 *
 * Split by discipline rather than answered once, for the reason the grade chart
 * is also drawn twice: a crag grades its boulders on Font and its routes on the
 * French scale, and "5c to 7B" is not a range — it is two rulers laid end to
 * end. Almost every crag has one discipline and so one range; the ones that
 * have both get both.
 *
 * Ordered by `difficulty_value`, which is the position on the scale rather than
 * the name — `5c` and `5+` do not sort as strings.
 */
export function gradeRanges(climbs: Climb[]): GradeRange[] {
  const byType = new Map<GradeRange['type'], Climb[]>();

  for (const climb of climbs) {
    // A line nobody has graded yet says nothing about how hard the place is.
    if (!climb.difficulty || climb.difficulty_value == null) continue;
    if (climb.type !== 'Route' && climb.type !== 'Boulder') continue;

    const group = byType.get(climb.type);
    if (group) group.push(climb);
    else byType.set(climb.type, [climb]);
  }

  // Routes first: on a crag they are the ordinary case, and a stable order
  // means the line does not reshuffle as climbs are added.
  return (['Route', 'Boulder'] as const).flatMap((type) => {
    const group = byType.get(type);
    if (!group) return [];

    const sorted = [...group].sort((a, b) => a.difficulty_value! - b.difficulty_value!);
    return [{ type, easiest: sorted[0].difficulty!, hardest: sorted[sorted.length - 1].difficulty! }];
  });
}

/** A range as one string: `6a` on its own when the ends meet, `5c – 7b` otherwise. */
export function formatGradeRange(range: GradeRange): string {
  return range.easiest === range.hardest ? range.easiest : `${range.easiest} – ${range.hardest}`;
}
